import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get, isEmpty, set } from "lodash";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { MdOutlineCalendarToday, MdOutlineFrontHand } from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { TERMINATION_RECIPIENTS } from "utils/api/graphql/queries/termination";
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  formProps,
  values,
  initialValues,
  setPercent,
  errors,
  isFieldRequired,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  // Second question: Contract subscribed for more than one year
  const contractMoreThanOneYear = get(
    initialValues,
    "project.fields.contractMoreThanOneYear",
    false,
  );

  // Third question: Does the member want Miltis to handle the termination?
  // selfOccupation = false means Miltis handles it, true means member handles it themselves
  const selfOccupation = get(
    initialValues,
    "project.fields.selfOccupation",
    undefined,
  );

  // Handle first question toggle (has existing contract)
  const handleHasExistingContractChange = (checked) => {
    set(initialValues, "project.ria", checked);
    set(initialValues, "project.fields.contractMoreThanOneYear", false);
    set(initialValues, "project.fields.selfOccupation", undefined);
    set(initialValues, "project.terminationRequest", null);
    setPercent(initialValues);
  };

  // Handle contract more than one year toggle
  const handleContractMoreThanOneYearChange = (checked) => {
    set(initialValues, "project.fields.contractMoreThanOneYear", checked);
    set(initialValues, "project.fields.selfOccupation", undefined);
    set(initialValues, "project.terminationRequest", null);
    setPercent(initialValues);
  };

  // Handle wants Miltis to handle termination toggle
  // If checked (Miltis handles), set selfOccupation to false
  // If unchecked (member handles), set selfOccupation to true
  const handleWantsMiltisToHandleChange = (checked) => {
    // checked = true means Miltis handles, so selfOccupation = false
    // checked = false means member handles, so selfOccupation = true
    set(initialValues, "project.fields.selfOccupation", !checked);
    if (checked) {
      set(initialValues, "project.terminationRequest", {
        id: cuid(),
        reason: "22",
        brokerageMandate: {
          id: cuid(),
        },
      });
      flushSync(() => {
        setDisabled((prev) => {
          setPercent(initialValues, {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: undefined,
            },
          });
          return {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: undefined,
            },
          };
        });
      });
    } else {
      set(initialValues, "project.terminationRequest", null);

      flushSync(() => {
        setDisabled((prev) => {
          setPercent(initialValues, {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: {
                recipient: true,
                dueDate: true,
                contractNumber: true,
              },
            },
          });
          return {
            ...prev,
            project: {
              ...prev.project,
              terminationRequest: {
                recipient: true,
                dueDate: true,
                contractNumber: true,
              },
            },
          };
        });
      });
    }
  };
  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
    >
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
        }
        subtitle={
          SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.SUBTITLE
        }
      >
        <Row gutter={[16, 16]}>
          {/* First question: Does the prospect currently have a complementary health insurance contract? */}
          <Col lg={24} xs={24}>
            <div className="d-flex justify--between align--center">
              <span>
                {SUBSCRIPTION.FORM.EXISTING_CONTRACT?.HAS_EXISTING_CONTRACT ||
                  "Le prospect dispose-t-il actuellement d'un contrat de complémentaire santé ?"}
              </span>
              <Switch
                checked={get(initialValues, "project.ria", false)}
                onChange={handleHasExistingContractChange}
              />
            </div>
          </Col>

          {/* Second question: Contract subscribed for more than one year - shown only if has existing contract */}
          {get(initialValues, "project.ria", false) && (
            <Col lg={24} xs={24}>
              <div className="d-flex justify--between align--center">
                <span>
                  {SUBSCRIPTION.FORM.EXISTING_CONTRACT
                    ?.CONTRACT_MORE_THAN_YEAR ||
                    "Contrat du prospect souscrit depuis plus d'un an"}
                </span>
                <Switch
                  checked={contractMoreThanOneYear}
                  onChange={handleContractMoreThanOneYearChange}
                />
              </div>
            </Col>
          )}

          {/* Third question: Does the member want Miltis to handle the termination - shown only if contract more than one year */}
          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear && (
              <Col lg={24} xs={24}>
                <div className="d-flex justify--between align--center">
                  <span>
                    {SUBSCRIPTION.FORM.EXISTING_CONTRACT
                      ?.WANTS_MILTIS_TO_HANDLE ||
                      "L'adhérent souhaite que Miltis s'occupe de la résiliation"}
                  </span>
                  <Switch
                    checked={selfOccupation === false}
                    onChange={handleWantsMiltisToHandleChange}
                  />
                </div>
              </Col>
            )}

          {/* Show info message when toggle is OFF */}
          {/* {!contractMoreThanOneYear && (
              <Col lg={24} xs={24}>
                <Alert
                  showIcon
                  type="info"
                  message={
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          SUBSCRIPTION.FORM.EXISTING_CONTRACT
                            ?.INFO_MESSAGE_LESS_THAN_YEAR ||
                          "Votre contrat ne pourra prendre effet que <strong>30 jours après votre souscription</strong>, ce qui entraînera une modification de la date d'effet initialement prévue.",
                      }}
                    />
                  }
                />
              </Col>
            )} */}

          {/* Contract info fields - shown when wants Miltis to handle termination */}
          {get(initialValues, "project.ria", false) &&
            contractMoreThanOneYear &&
            selfOccupation === false && (
              <>
                <Col lg={24} xs={24}>
                  <Input
                    label={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT
                        ?.CURRENT_EFFECTIVE_DATE?.LABEL ||
                      "Date d'effet du contrat actuel du prospect"
                    }
                    placeholder={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT
                        ?.CURRENT_EFFECTIVE_DATE?.PLACEHOLDER || "JJ/MM/AAAA"
                    }
                    type="date"
                    variant="simple"
                    format={DATE_FORMAT}
                    disabledDate={(current) => {
                      return current && current > dayjs();
                    }}
                    defaultValue={(() => {
                      const dateValue = get(
                        initialValues,
                        "project.terminationRequest.dueDate",
                      );
                      if (!dateValue) return undefined;
                      // Try parsing with API format first, then fallback to default parsing
                      const parsedDate = dayjs(
                        dateValue,
                        API_DATE_FORMAT,
                        true,
                      ).isValid()
                        ? dayjs(dateValue, API_DATE_FORMAT, true)
                        : dayjs(dateValue);
                      return parsedDate.isValid() ? parsedDate : undefined;
                    })()}
                    icon={<MdOutlineCalendarToday size={16} />}
                    onChange={(date, _) => {
                      set(
                        initialValues,
                        "project.terminationRequest.dueDate",
                        date
                          ? date
                              .set("hour", 4)
                              .set("minute", 0)
                              .set("second", 0)
                          : date,
                      );
                    }}
                    onBlur={() => setPercent(initialValues)}
                    required
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <Input
                    label={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER
                        ?.LABEL || "N° de contrat"
                    }
                    placeholder={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CONTRACT_NUMBER
                        ?.PLACEHOLDER || "ex : 123456789"
                    }
                    icon={<MdOutlineFrontHand size={16} />}
                    defaultValue={get(
                      initialValues,
                      "project.terminationRequest.contractNumber",
                    )}
                    errors={errors["project.terminationRequest.contractNumber"]}
                    touched={
                      !isEmpty(
                        errors["project.terminationRequest.contractNumber"],
                      )
                    }
                    onChange={({ target: { value } }) => {
                      const filteredValue = filterContractNumber(value);
                      set(
                        initialValues,
                        "project.terminationRequest.contractNumber",
                        filteredValue,
                      );
                    }}
                    onBlur={({ target: { value } }) => {
                      const trimmedValue = value.trim();
                      set(
                        initialValues,
                        "project.terminationRequest.contractNumber",
                        trimmedValue,
                      );
                      setPercent(initialValues);
                    }}
                    required
                  />
                </Col>
                <Col lg={24} xs={24}>
                  <Select
                    type="async"
                    node={{
                      query: TERMINATION_RECIPIENTS,
                      variables: {},
                      accessors: {
                        root: "terminationRecipients.data",
                        label: "name",
                        value: "id",
                      },
                    }}
                    label={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY
                        ?.LABEL || "Nom de l'assureur actuel du prospect"
                    }
                    placeholder={
                      SUBSCRIPTION.FORM.EXISTING_CONTRACT?.CURRENT_COMPANY
                        ?.PLACEHOLDER || "Sélectionner une compagnie"
                    }
                    defaultValue={get(
                      initialValues,
                      "project.terminationRequest.recipient",
                    )}
                    errors={errors["project.terminationRequest.recipient"]}
                    touched={
                      !isEmpty(errors["project.terminationRequest.recipient"])
                    }
                    onChange={(value, option) => {
                      set(
                        initialValues,
                        "project.terminationRequest.recipient",
                        value,
                      );
                      set(
                        initialValues,
                        "project.terminationRequest.brokerageMandate.currentCompany",
                        option?.name || value || "",
                      );
                      setPercent(initialValues);
                    }}
                    required
                  />
                </Col>
              </>
            )}
        </Row>
      </Form>
    </div>
  );
};

export default TerminationRequest;
